import React, { useState } from 'react';
import { BookOpen, Search, ArrowUpRight, Calculator, Award } from 'lucide-react';
import { AppView } from '../../types';
import { seoCmsEngine } from '../../services/seoCmsEngine';

interface Props {
  onNavigateToTool?: (view: AppView) => void;
}

export const GlossaryHub: React.FC<Props> = ({ onNavigateToTool }) => {
  const [terms] = useState(seoCmsEngine.getGlossaryTerms());
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTerms = terms.filter(
    (t) =>
      t.localizedTerm.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.definition.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.canonicalConcept.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-8 pb-12">
      {/* Banner */}
      <div className="bg-slate-900 text-white rounded-2xl p-8 shadow-xl space-y-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-semibold border border-emerald-500/30">
          <BookOpen className="w-3.5 h-3.5" />
          YamooPay Financial Knowledge Base
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight">Global Financial Terminology Glossary</h1>
        <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
          Canonical definitions and localized financial terms across jurisdictions, verified by certified reviewers.
        </p>
      </div>

      {/* Search */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <div className="relative w-full max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search financial terms (e.g. Net Pay, APRC, Mid-Market)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          />
        </div>
      </div>

      {/* Term Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredTerms.map((term) => (
          <div key={term.id} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="font-bold text-slate-900 text-base">{term.localizedTerm}</h3>
                <span className="text-[11px] text-emerald-700 font-semibold">{term.canonicalConcept}</span>
              </div>
              <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-bold">
                {term.market}
              </span>
            </div>

            <p className="text-slate-600 text-xs leading-relaxed">{term.definition}</p>

            <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
              <span className="font-semibold text-slate-400">Synonyms:</span>
              {term.synonyms.map((s) => (
                <span key={s} className="px-2 py-0.5 rounded bg-slate-50 border border-slate-200 font-medium">
                  {s}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between border-t border-slate-100 pt-3 text-xs">
              <span className="text-slate-400">Reviewed by {term.reviewer}</span>
              {term.relatedToolId && (
                <button
                  onClick={() => onNavigateToTool?.(term.relatedToolId!)}
                  className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-bold"
                >
                  <Calculator className="w-3.5 h-3.5" /> Launch Tool <ArrowUpRight className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
